'use client';

import type { ReactNode } from 'react';

/** The same navy gradient band the locations sheet uses for its header. */
export const SHEET_HEADER_GRADIENT =
  'linear-gradient(135deg, #1c2b39 0%, #243848 50%, #2d4a5c 100%)';

interface SheetHeaderProps {
  title: ReactNode;
  /** Small line under the title */
  subtitle?: ReactNode;
  /** Something above the title, e.g. a status badge */
  eyebrow?: ReactNode;
  /** An icon shown in a white disc beside the title */
  icon?: ReactNode;
  className?: string;
  /** Override the navy band; `null` leaves the background to className (See Jesus texture) */
  background?: string | null;
  /** Something painted over the band, e.g. a logo in a corner */
  decoration?: ReactNode;
  /** Extra classes for the title (See Jesus: foil-clipped text) */
  titleClassName?: string;
}

export function SheetHeader({
  title,
  subtitle,
  eyebrow,
  icon,
  className = '',
  background,
  decoration,
  titleClassName = '',
}: SheetHeaderProps) {
  return (
    <div
      className={`relative px-6 pt-7 pb-6 text-white sm:px-8 sm:pt-8 ${
        // The band clips its own decoration; a See Jesus header lets the
        // corner logo reach past it to the panel's edge.
        background === null ? '' : 'overflow-hidden'
      } ${className}`}
      style={background === null ? undefined : { background: background ?? SHEET_HEADER_GRADIENT }}
    >
      {decoration}
      <div className="relative z-10 flex items-start gap-4">
        {icon && (
          <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-white shadow-sm">
            {icon}
          </span>
        )}
        <div className="min-w-0 flex-1">
          {eyebrow && <div className="mb-2">{eyebrow}</div>}
          <h2
            className={
              titleClassName ||
              'text-2xl leading-tight font-extrabold tracking-tight uppercase sm:text-3xl'
            }
          >
            {title}
          </h2>
          {subtitle && <p className="mt-2 text-sm font-medium text-white/75">{subtitle}</p>}
        </div>
      </div>
    </div>
  );
}
